import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Stock shadcn/ui `buttonVariants` (new-york) for data tables and pagination —
 * native `<button>` only; avoids Tessera `ui/button` and `[data-slot="button"]` globals.
 */
const dataTableButtonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        outline:
          "border border-input bg-background shadow-none hover:bg-accent hover:text-accent-foreground",
        ghost: "hover:bg-accent hover:text-accent-foreground",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "outline",
      size: "default",
    },
  },
);

export type DataTableButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof dataTableButtonVariants>;

const DataTableButton = React.forwardRef<HTMLButtonElement, DataTableButtonProps>(
  function DataTableButton(
    { className, variant, size, type = "button", ...props },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        data-slot="data-table-button"
        data-variant={variant ?? "ghost"}
        data-size={size ?? "default"}
        className={cn(dataTableButtonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
DataTableButton.displayName = "DataTableButton";

export { DataTableButton, dataTableButtonVariants };
